// Read-only access to the captured site content. `content.json` is generated by
// scripts/capture/prepare-content.py; never edit it by hand.
import content from "./content.json";
// Generated by scripts/capture/image-variants.py.
import variants from "./image-variants.json";
import { blogPosts, writtenPages } from "./written";
// Paths taken off the site (old posts, departed team members' pages…).
import removed from "../../content/removed.json";

// A few captured posts lost their heading markup: short lines with no closing
// punctuation ("1. Inaccurate Time Tracking") were saved as paragraphs.
const looksLikeHeading = ({ type, text = "" }) =>
  type === "p" &&
  text.trim().split(/\s+/).length <= 8 &&
  !/[.!?:,;)"”]$/.test(text.trim());

const captured = content.pages.map((page) =>
  page.body?.some(looksLikeHeading)
    ? {
        ...page,
        body: page.body.map((block) =>
          looksLikeHeading(block) ? { ...block, type: "h2" } : block,
        ),
      }
    : page,
);

const written = writtenPages(new Set(captured.map((page) => page.path)));
const writtenPaths = new Set(written.map((page) => page.path));

const isRemoved = (path) => removed.includes(path);
const cardPath = (card) => new URL(card.href, "https://xeratec.com").pathname;

/** Every page on the site: captured ones, then new and replacement pages. */
export const pages = [
  ...captured.filter((page) => !writtenPaths.has(page.path)),
  ...written,
].filter((page) => !isRemoved(page.path));

/** Source URL → local asset path (relative to the public directory). */
export const assets = content.assets;

/** Same-aspect WordPress sizes of each downloaded image, for srcset. */
export const imageVariants = variants;

const pagesByPath = new Map(pages.map((page) => [page.path, page]));

/** Look up a page by its normalized path, resolving duplicate-body aliases. */
export function pageAt(path) {
  const page = pagesByPath.get(path);
  return page?.alias ? { ...pageAt(page.alias), ...page } : page;
}

/** Cards from every page under `prefix`, de-duplicated by link. */
function collectCards(prefix) {
  const cards = captured
    .filter((page) => page.path.startsWith(prefix))
    .flatMap((page) => page.cards || [])
    .filter((card) => !isRemoved(cardPath(card)));
  return [...new Map(cards.map((card) => [cardPath(card), card])).values()];
}

export const caseStudies = collectCards("/case-studies/");
/** Blog cards: new posts (newest first), then the captured ones. */
export const insights = [
  ...blogPosts.map(({ title, path, image, date }) => ({
    title,
    href: path,
    image,
    date,
  })),
  // An old post rewritten as a file in content/blog/ is listed once.
  ...collectCards("/case-studies-2/").filter(
    (card) => !writtenPaths.has(cardPath(card)),
  ),
];
export const teamMembers = pageAt("/about-us/")?.cards || [];

export const ebookPdf = Object.keys(assets).find((url) => url.endsWith(".pdf"));

/**
 * A page's display title. A few captured headings are generic labels
 * ("Case Study", "Blog"); those fall back to the page title, minus the
 * " Archives" suffix WordPress adds to tag and category pages.
 */
export function pageTitle(page) {
  const heading = page?.heading?.trim();
  if (heading && !/^(case study|blog)$/i.test(heading)) return heading;
  const title = (page?.title || heading || "").replace(/ Archives$/, "");
  return title.charAt(0).toUpperCase() + title.slice(1);
}
